<template>
  <v-app>
    <EcsSpinner v-if="isLoading" />
    <div v-if="!isLoading && !logout" class="app-container">
      <EcsAppBar />
      <v-main class="app-container">
        <router-view />
      </v-main>
      <EcsThemeMultipleBps />
      <EcsOverrideThemeBpsDialog />
    </div>
    <EcsLogin v-if="logout" />
    <EcsSnackbarList />
    <EcsContextMenu />
    <EcsConfirmationDialog />
    <EcsListSelectionDialog />
    <EcsContactSelectionDialog />
  </v-app>
</template>

<script>
import EcsAppBar from '@/components/ecs-app-bar/EcsAppBar';
import EcsLogin from '@/components/ecs-login/EcsLogin';
import EcsSpinner from '@/components/ecs-spinner/EcsSpinner';
import EcsSnackbarList from '@/components/ecs-snackbar/EcsSnackbarList';
import EcsThemeMultipleBps from '@/components/ecs-theme-multiple-bps/EcsThemeMultipleBps';
import EcsOverrideThemeBpsDialog from '@/components/ecs-override-theme-bps-dialog/EcsOverrideThemeBpsDialog';
import EcsContextMenu from '@/components/ecs-context-menu/EcsContextMenu';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsListSelectionDialog from '@/components/ecs-list-selection-dialog/EcsListSelectionDialog';
import EcsContactSelectionDialog from '@/components/ecs-contact-selection-dialog/EcsContactSelectionDialog';
import { DEFAULT_URI } from '@/constants';
import { mapActions, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';

export default {
  name: 'App',

  components: {
    EcsAppBar,
    EcsConfirmationDialog,
    EcsContactSelectionDialog,
    EcsContextMenu,
    EcsListSelectionDialog,
    EcsLogin,
    EcsOverrideThemeBpsDialog,
    EcsSpinner,
    EcsSnackbarList,
    EcsThemeMultipleBps
  },

  data() {
    return {
      isLoading: true,
      logout: false
    };
  },

  computed: mapGetters(['organizationId']),

  methods: {
    ...mapActions(['getStoredTheme', 'login', 'getBusinessProcesses', 'getCustomizedBusinessProcesses', 'getChatUrl']),

    handleFailedLogin() {
      this.isLoading = false;
      this.logout = true;
      sessionStorage.removeItem('sessionId');
    },

    handleSuccessfulLogin() {
      this.isLoading = false;
      this.getChatUrl();
      this.getBusinessProcesses();
      this.getCustomizedBusinessProcesses();
      setTimeout(() => {
        this.getStoredTheme({
          payload: { organizationId: this.organizationId, uri: DEFAULT_URI },
          vuetify: this.$vuetify
        });
      }, 300);
      if (!isEmpty(this.$route.query)) {
        this.$router.push(this.$route.path);
      }
    }
  },

  mounted() {
    const jwt = this.$route.query.jwt || sessionStorage.getItem('jwt');
    const options = { headers: { Authorization: `Bearer ${jwt}` } };
    sessionStorage.setItem('jwt', jwt);
    this.login(options)
      .then(this.handleSuccessfulLogin)
      .catch(this.handleFailedLogin);
  }
};
</script>
<style lang="scss">
$heading-font-family: 'Poppins', sans-serif;
$body-font-family: 'Roboto', Helvetica, Arial, sans-serif;
</style>

<style>
.body::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
/*Custom Scrollbar styles for Firefox*/
.custom-scrollbar {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #b8b8b8 #4d4d4d;
}
/*Custom Scrollbar styles for Chrome, Safari*/
*::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 6px;
}

*::-webkit-scrollbar {
  width: 6px;
  height: 6px;
  background-color: transparent;
}

*::-webkit-scrollbar-thumb {
  border-radius: 6px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #cccccc;
}

*::-webkit-scrollbar-corner {
  background: transparent;
}

.v-card__title,
.v-card__subtitle {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-style: normal;
}

[class*='poppins-'],
.xtium-title,
.xtium-subtitle,
.xtium-title-light,
.xtium-title-regular,
.xtium-title-bold {
  font-family: 'Poppins', sans-serif;
  font-style: normal;
}

.poppins-light,
.xtium-title-light {
  font-weight: 300;
}

.poppins-regular,
.xtium-title-regular {
  font-weight: 400;
}

.poppins-bold,
.xtium-title-bold {
  font-weight: 700;
}

.xtium-title {
  font-size: 18px;
}

.xtium-subtitle {
  font-size: 14px;
  opacity: 0.75;
}

.light-surface {
  background-color: var(--v-custom-surface-base) !important;
}

.lighter-surface {
  background-color: var(--v-custom-surface-lighten1) !important;
}

.darken-surface {
  background-color: var(--v-custom-surface-darken1) !important;
}

.background-surface-base {
  background-color: var(--v-surface-base) !important;
}

.divider {
  background-color: var(--v-primary-base) !important;
}

.drop-shadow-down {
  filter: drop-shadow(0px 10px 2px var(--v-custom-surface-darken1)) !important;
}

.drop-shadow-up {
  filter: drop-shadow(0px -10px 2px var(--v-custom-surface-darken1)) !important;
}

.primary-title {
  text-transform: uppercase;
  color: var(--v-primary-base);
  margin-bottom: 16px;
}

.app-container {
  height: 100%;
}

.full-height {
  height: 100% !important;
}

.full-width {
  width: 100% !important;
}

.height-fit-content {
  height: fit-content !important;
}

.text-align-center {
  text-align: center;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
